import type { AuthExchangeResponse, AuthRefreshResponse } from '../../types/index.js';
import type { RequestFn } from '../http.js';
import { LOGOUT_STATUS_TOKEN_HEADER, logoutOperationSchema, type LogoutOperation, type LogoutOperationStatus, type LogoutResponse } from '../../schemas/logoutOperation.js';

/** Auth surface: platform-token exchange, the public config probe, the
 *  pre-platform test login, and logout. */
export function makeAuthApi(request: RequestFn) {
  return {
    exchangeAccessToken: (accessToken: string) =>
      request<AuthExchangeResponse>('/api/auth/exchange', {
        method: 'POST',
        body: JSON.stringify({ accessToken }),
      }),

    /** Public probe — tells the client which optional auth surfaces the server
     *  has enabled: the pre-platform test login, and Cispracs ID sign-in
     *  (`oidcLogin`; absent on older servers, read as false). */
    authConfig: () => request<{ testLoginEnabled: boolean; oidcLogin?: boolean }>('/api/auth/config'),

    /** Pre-platform test login. Creates a throwaway User on the server and
     *  issues a session. Only succeeds when the server has ENABLE_TEST_LOGIN=true;
     *  otherwise the endpoint 404s. */
    testLogin: (displayName: string) =>
      request<AuthExchangeResponse>('/api/auth/test-login', {
        method: 'POST',
        body: JSON.stringify({ displayName }),
      }),

    /** Native clients only: exchange a long-lived refresh token for a fresh
     *  access+refresh pair (rotation). On 401 the client must re-login. */
    refreshSession: (refreshToken: string) =>
      request<AuthRefreshResponse>('/api/auth/refresh', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      }),

    logout: () => request<LogoutResponse>('/api/auth/logout', { method: 'POST' }),

    /** The minted capability remains usable after logout revokes the cookie.
     * Cookies and native Bearer defaults grant no authority on this endpoint. */
    logoutOperationStatus: (operation: LogoutOperation) => {
      const { id, statusToken } = logoutOperationSchema.parse(operation);
      return request<LogoutOperationStatus>(`/api/auth/logout/status/${encodeURIComponent(id)}`, {
        headers: { [LOGOUT_STATUS_TOKEN_HEADER]: statusToken },
      });
    },
  };
}
